import classNames from "classnames";
import React from "react";
import Transition from "react-transition-group/Transition";

import { useFocusBlur } from "../../../hooks/useFocusBlur";
import styles from "./index.module.scss";
import { Icon } from "../../atoms/icon";
import { Input } from "../../atoms/input";
import { useDevice } from "../../../contexts/deviceContext";
import ReactiveList from "../../atoms/reactiveList";

const NullItemLabel = "- - - -";

export function withNullOption<Item>(
  c: DropDownOptions<Item>,
  nullItemLabel = NullItemLabel,
): DropDownOptions<Item | null> {
  return {
    options: [null, ...c.options],
    renderLabel: (item) => (item ? c.renderLabel(item) : nullItemLabel),
    toStr: (item) => (item ? c.toStr(item) : ""),
    fromStr: (s) => (s === "" ? null : c.fromStr(s)),
  };
}

export type DropDownOptions<Item> = {
  options: Array<Item>;
  toStr: (item: Item) => string;
  fromStr: (s: string) => Item;
  renderLabel: (item: Item) => string;
};

type Props<Item> = DropDownOptions<Item> & {
  value: Item;
  setValue: (item: Item) => void;
  status: "default" | "disabled" | "error";
  onBlur: () => void;
  className?: string;
  type?: "normal" | "medium" | "arrange";
  headless?: boolean;
  label?: string;
  width?: string;
  nullItemlabel?: string;
  onDirty?: () => void;
};

export function DropDown<Item>(props: Props<Item>) {
  const {
    className,
    headless,
    label,
    type = "normal",
    onBlur,
    status,
    value,
    setValue,
    toStr,
    fromStr,
    renderLabel,
    options,
    width,
    onDirty,
  } = props;

  const { isFocused, controlFocus } = useFocusBlur({
    onBlur,
  });
  const device = useDevice();
  const isMobile = device === "mobile";

  // TODO: improve readability
  const caret = status !== "disabled" && (
    <div className={styles.caret}>
      <Icon
        className={classNames(styles.caretIcon, {
          [styles.active]: !isMobile && isFocused,
        })}
        name="chevron_down"
        color={
          status === "error"
            ? "error"
            : !isMobile && isFocused
            ? "accent"
            : "black"
        }
        size="large"
      />
    </div>
  );

  return (
    <div className={classNames(styles.container, className)}>
      <Input
        headless={headless}
        status={status}
        hasValue={Boolean(value)}
        isFocused={!isMobile && isFocused}
        label={label}
        alwaysShowLabel
        width={width}
      >
        {isMobile ? (
          <>
            {caret}
            <select
              className={classNames(styles.select, styles[type], {
                [styles.disabled]: status === "disabled",
              })}
              disabled={status === "disabled"}
              value={toStr(value) || ""}
              onChange={(e) => {
                setValue(fromStr(e.target.value));
                controlFocus.onBlur();
                onDirty?.();
              }}
              {...controlFocus}
            >
              {options.map((item, i) => (
                <option key={i} value={toStr(item)}>
                  {renderLabel(item)}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <div
              className={classNames(styles.value, styles[type], {
                [styles.disabled]: status === "disabled",
              })}
              tabIndex={0}
              onClick={() => {
                if (status !== "disabled") {
                  if (isFocused) {
                    controlFocus.onBlur();
                  } else {
                    controlFocus.onFocus();
                  }
                }
              }}
              onBlur={controlFocus.onBlur}
            >
              {caret}
              {renderLabel(value)}
            </div>
            <Transition
              in={status !== "disabled" && isFocused}
              timeout={{ enter: 0, exit: 300 }}
              mountOnEnter
              unmountOnExit
            >
              {(transitionStatus: string) => (
                <div className={styles.dropDown}>
                  <ReactiveList
                    options={options}
                    renderLabel={renderLabel}
                    onItemClick={(v) => {
                      if (v !== value) {
                        onDirty?.();
                      }
                      setValue(v);
                      controlFocus.onBlur();
                    }}
                    status={transitionStatus}
                  />
                </div>
              )}
            </Transition>
          </>
        )}
      </Input>
    </div>
  );
}
